import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
    fetchCatalogItems,
    fetchGlobalCatalogItems,
} from 'entities/CatalogItem';
import { GLOBAL_CATALOG_ITEMS_PAGE_SIZE } from 'entities/CatalogItem/model/consts/catalogItemsConsts';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { categoryActions } from 'entities/Category/model/slices/CategorySlice';
import {
    getSelectedPropertyCatalogId,
    getSelectedPropertyId,
} from 'entities/Property/model/selectors/PropertySelectors';
import { addCatalogItemActions } from 'features/AddCatalogItem/model/slices/AddCatalogItemSlice';
import { getSearchLocalCatalogItemValue } from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import { searchCatalogItemsActions } from 'features/SearchCatalogItems/model/slices/SearchCatalogItemsSlice';
import {
    getRouteGlobalCatalog,
    getRouteOrdersAddItemsCategories,
    getRouteOrdersAddItemsMarketplace,
    getRoutePropertiesCatalog,
} from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useFetchWIthController } from 'shared/lib/hooks/useFetchWIthController';

type NavigationType = 'global-marketplace' | 'property-catalog';

export const useMarketplaceNavigation = (
    type: NavigationType = 'global-marketplace',
    isOrdersAddItemsFlow = false,
) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const localSearchValue = useSelector(getSearchLocalCatalogItemValue);
    const catalogId = useSelector(getSelectedPropertyCatalogId);
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const fetchWithController = useFetchWIthController();
    const navigateToMarketplace = useCallback(() => {
        const propertyId =
            params.propertyId ||
            location.state?.selectedPropertyId ||
            selectedPropertyId;

        if (type === 'global-marketplace') {
            // From Property Catalog
            if (localSearchValue) {
                dispatch(searchCatalogItemsActions.setSkipReset(true));
            }
            dispatch(searchCatalogItemsActions.setActive(true));
            dispatch(addCatalogItemActions.setEditItemMode(true));
            dispatch(
                searchCatalogItemsActions.setGlobalCatItemSearchValue(
                    localSearchValue,
                ),
            );
            dispatch(categoryActions.clearCategorySelection());

            if (isOrdersAddItemsFlow) {
                navigate(getRouteOrdersAddItemsMarketplace(), {
                    state: location.state,
                });
            } else if (propertyId) {
                navigate(getRouteGlobalCatalog(propertyId));
            }

            if (localSearchValue) {
                fetchWithController((controller: AbortController) =>
                    dispatch(
                        fetchGlobalCatalogItems({
                            searchString: localSearchValue,
                            page: 1,
                            pageSize: GLOBAL_CATALOG_ITEMS_PAGE_SIZE,
                            propertyId,
                            abortController: controller,
                        }),
                    ),
                );
            }
        } else {
            // From Inventory
            if (localSearchValue) {
                dispatch(searchCatalogItemsActions.setSkipReset(true));
                dispatch(searchCatalogItemsActions.setActive(true));
                dispatch(
                    searchCatalogItemsActions.setLocalCatItemSearchValue(
                        localSearchValue,
                    ),
                );
                dispatch(catalogItemActions.setLoading(true));
            }

            if (isOrdersAddItemsFlow) {
                navigate(getRouteOrdersAddItemsCategories(), {
                    state: location.state,
                });
            } else if (propertyId) {
                navigate(getRoutePropertiesCatalog(propertyId));
            }

            if (localSearchValue && catalogId) {
                fetchWithController((controller: AbortController) =>
                    dispatch(
                        fetchCatalogItems({
                            data: {
                                catalogId,
                                searchStr: localSearchValue,
                                isInventory: false,
                            },
                            abortController: controller,
                        }),
                    ),
                );
            }
        }
    }, [
        dispatch,
        navigate,
        params.propertyId,
        localSearchValue,
        catalogId,
        selectedPropertyId,
        fetchWithController,
        type,
        isOrdersAddItemsFlow,
        location.state,
    ]);

    return { navigateToMarketplace };
};
